// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "From Konvo" on Home. Where the From Konvo list is on (docs/konvo-assistant/ready-list.md),
// Home shows the same entries as the corner list (ReadyList, GET /konvo/corner/list): corner ready
// cards and the engine's briefs and nudges, in the list's order, so acting on or dismissing one
// in either place takes it out of both. The list carries no brief blocks, so this store still
// reads the engine's items (GET /konvo/assistant/engine/today): they draw an engine entry's
// richer row and inline card, and the morning checklist ("things for today", never in the list).
// Where the list is off (or has not loaded yet), Home shows the engine's items as before.
// The server already leaves out what was dismissed, marked unwanted or done (see EngineHome.ts);
// this store also hides, at once, what was dismissed or acted on here and a checklist whose last
// to-do was just ticked.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import BriefChecklist from '@app/features/konvo_assistant/state/BriefChecklist';
import KonvoConnections, {type KonvoProactiveTriggerKind} from '@app/features/konvo_connections/state/KonvoConnections';
import ReadyList from '@app/features/konvo_corner/state/ReadyList';
import {http} from '@app/features/platform/transport/RestTransport';
import type {ReadyItem} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import type {AssistantBriefBlock, AssistantResponseBlock} from '@konvo/schema/src/domains/konvo/AssistantResponse';
import {makeAutoObservable, observable, runInAction} from 'mobx';

export interface HomeProactiveItem {
	item_id: string;
	kind: KonvoProactiveTriggerKind;
	created_at: string;
	outcome: 'used' | 'dismissed' | 'bad' | null;
	/** `blocks[0]` is the brief; a morning brief may carry a timeline after it. */
	blocks: Array<AssistantResponseBlock>;
	/** Which to-dos of a checklist brief are ticked off. */
	done?: Array<number>;
}

/** Home shows at most this many; the rest are in the From Konvo list (or the Assistant DM). */
export const HOME_PROACTIVE_LIMIT = 3;
/** A checklist ticked off completely stays this long, so the last tick is seen before the card goes. */
export const FINISHED_CHECKLIST_MS = 1200;

function isBrief(block: AssistantResponseBlock | undefined): block is AssistantBriefBlock {
	return block?.type === 'brief';
}

function createdAt(item: HomeProactiveItem): number {
	const at = Date.parse(item.created_at);
	return Number.isNaN(at) ? 0 : at;
}

/** Pure: a checklist brief whose to-dos are all ticked off. */
export function isChecklistDone(item: HomeProactiveItem, done: ReadonlySet<number>): boolean {
	const todos = isBrief(item.blocks[0]) ? item.blocks[0].todos : undefined;
	return !!todos?.length && todos.every((_, index) => done.has(index));
}

/**
 * What Home shows: well-formed briefs only, newest first, each item once,
 * nothing the user put away or finished, and no more than the limit.
 */
export function visibleProactiveItems(
	items: ReadonlyArray<HomeProactiveItem>,
	dismissed: ReadonlySet<string>,
	limit = HOME_PROACTIVE_LIMIT,
): Array<HomeProactiveItem> {
	const seen = new Set<string>();
	return items
		.filter((item) => {
			if (!item.item_id || seen.has(item.item_id)) return false;
			seen.add(item.item_id);
			if (item.outcome === 'dismissed' || item.outcome === 'bad' || dismissed.has(item.item_id)) return false;
			return Array.isArray(item.blocks) && isBrief(item.blocks[0]);
		})
		.map((item, index) => ({item, index}))
		.sort((left, right) => createdAt(right.item) - createdAt(left.item) || left.index - right.index)
		.slice(0, limit)
		.map(({item}) => item);
}

/** One From Konvo entry on Home from the shared list, with the engine's item when it is an engine brief or nudge. */
export interface HomeListEntry {
	entry: ReadyItem;
	/** The engine's item for `entry.engine_item_id` (its blocks), once engine/today has it. */
	engine: HomeProactiveItem | null;
}

/**
 * Pure: what Home shows from the From Konvo list: its entries in its order (upcoming meetings
 * first, then newest first), less engine items put away or finished here and checklists (those
 * are "things for today"), each with its engine item when there is one, and no more than the limit.
 */
export function homeListEntries(
	entries: ReadonlyArray<ReadyItem>,
	engineItems: ReadonlyArray<HomeProactiveItem>,
	dismissed: ReadonlySet<string>,
	limit = HOME_PROACTIVE_LIMIT,
): Array<HomeListEntry> {
	const byId = new Map<string, HomeProactiveItem>();
	for (const item of engineItems) if (item.item_id && !byId.has(item.item_id)) byId.set(item.item_id, item);
	const seen = new Set<string>();
	const shown: Array<HomeListEntry> = [];
	for (const entry of entries) {
		if (shown.length >= limit) break;
		if (!entry.id || seen.has(entry.id) || entry.state !== 'ready') continue;
		seen.add(entry.id);
		const engineId = entry.engine_item_id;
		if (engineId && dismissed.has(engineId)) continue;
		const engine = engineId ? (byId.get(engineId) ?? null) : null;
		if (engine && (engine.outcome === 'dismissed' || engine.outcome === 'bad')) continue;
		const block = engine?.blocks?.[0];
		if (isBrief(block) && block.todos?.length) continue;
		shown.push({entry, engine: engine && isBrief(block) ? engine : null});
	}
	return shown;
}

class HomeProactiveStore {
	items: Array<HomeProactiveItem> = [];
	/** The first refresh has answered (or failed): Home knows what the engine has for today. */
	loaded = false;
	/**
	 * Dismissed or finished on this device this session (a nudge acted on, a
	 * checklist ticked off), so a refresh that raced the server cannot bring one back.
	 */
	dismissed = observable.set<string>();
	private inFlight: Promise<void> | null = null;
	private finishing = new Set<string>();

	constructor() {
		makeAutoObservable<this, 'inFlight' | 'finishing'>(this, {inFlight: false, finishing: false});
	}

	get visible(): Array<HomeProactiveItem> {
		return visibleProactiveItems(this.items, this.dismissed);
	}

	/**
	 * From Konvo on Home from the shared list, once the list has loaded; null while it is off for
	 * this person (it never starts) or not loaded yet: Home then shows `visible` as before.
	 */
	get listEntries(): Array<HomeListEntry> | null {
		if (!ReadyList.loaded) return null;
		return homeListEntries(ReadyList.items, this.items, this.dismissed);
	}

	/** Checklists on Home with every to-do ticked, here or by the server. */
	get finishedChecklists(): Array<string> {
		return this.visible
			.filter((item) => isChecklistDone(item, BriefChecklist.get(item.item_id)))
			.map((item) => item.item_id);
	}

	/** Best effort: a failed request keeps whatever Home already shows. */
	refresh(): Promise<void> {
		if (this.inFlight) return this.inFlight;
		this.inFlight = http
			.get<{items?: Array<HomeProactiveItem>}>(Endpoints.KONVO_ASSISTANT_ENGINE_TODAY, {mode: 'silent'})
			.then((response) => {
				const items = response.body?.items;
				if (Array.isArray(items)) runInAction(() => this.setItems(items));
			})
			.catch(() => undefined)
			.finally(() => {
				this.inFlight = null;
				runInAction(() => {
					this.loaded = true;
				});
			});
		return this.inFlight;
	}

	setItems(items: Array<HomeProactiveItem>): void {
		this.items = items;
		for (const item of items) if (item.item_id && item.done) BriefChecklist.seed(item.item_id, item.done);
	}

	/** Gone from Home (and the From Konvo list) at once; the engine hears about it in the background. */
	dismiss(itemId: string): void {
		this.dismissed.add(itemId);
		KonvoConnections.reportProactiveOutcome(itemId, 'dismissed');
		this.leaveList(itemId);
	}

	/** Done with, e.g. a nudge whose action was just used (the card reports that itself). */
	finish(itemId: string): void {
		this.dismissed.add(itemId);
		this.leaveList(itemId);
	}

	/** The From Konvo list's entry for this engine item leaves it too, and the list looks again shortly. */
	private leaveList(itemId: string): void {
		const entry = ReadyList.items.find((item) => item.engine_item_id === itemId);
		if (!entry) return;
		ReadyList.remove(entry);
		ReadyList.refreshSoon(1500);
	}

	/** Leaves after a beat, unless a to-do is unticked again meanwhile. */
	finishSoon(itemId: string, delayMs = FINISHED_CHECKLIST_MS): void {
		if (this.finishing.has(itemId)) return;
		this.finishing.add(itemId);
		setTimeout(() => {
			this.finishing.delete(itemId);
			if (this.finishedChecklists.includes(itemId)) this.finish(itemId);
		}, delayMs);
	}
}

export default new HomeProactiveStore();
